import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "components/button";
import Icon from "components/icon";
import DateRangePicker from "components/date-range-picker";
import { Columns, TableSortState } from "components/table";
import ActionMenu from "components/shared/action-menu";
import { toast } from "react-toastify";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import useHistoryPanel from "hooks/shared/useHistoryPanel";
import { useAppTranslation } from "locale/useAppTranslation";
import { errorHandler } from "_services/errorHandler";
import { editTripDaysThresholdPageRouteName } from "../edit/edit.page";
import {
  useTripDaysServiceV1ListTripDaysThresholdsQuery,
  useTripDaysServiceV1ActivateTripDaysThresholdsMutation,
  useTripDaysServiceV1DeactivateTripDaysThresholdsMutation,
  type TripDaysV1TripDaysThresholdRes,
} from "_services/modules/trip_days";

const sortFieldMap: Record<string, string> = {
  tripDaysName: "trip_days_threshold_name",
  durationCondition: "duration_condition",
  equivalentDays: "equivalent_days",
  status: "status",
  createdAt: "created_at",
  updatedAt: "updated_at",
};

export interface TripDaysThresholdRow {
  id: string;
  tripDaysName: string;
  durationCondition: string;
  durationMin: number | null;
  durationMax: number | null;
  equivalentDays: number;
  description: string;
  status: "active" | "inactive";
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
}

const formatDuration = (row: TripDaysThresholdRow, hoursUnit: string): string => {
  if (row.durationCondition === "below") {
    return `< ${row.durationMax} ${hoursUnit}`;
  }
  if (row.durationCondition === "between") {
    return `${row.durationMin} ${hoursUnit} - ${row.durationMax} ${hoursUnit}`;
  }
  if (row.durationCondition === "above") {
    return `> ${row.durationMin} ${hoursUnit}`;
  }
  return "-";
};

const formatEquivalentDays = (days: number, dayUnit: string, daysUnit: string): string => {
  return `${days} ${days !== 1 ? daysUnit : dayUnit}`;
};

const useTripDaysThresholdList = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("tripDaysThreshold");
  const [selectedKeys, setSelectedKeys] = useState<Array<string | number>>([]);
  const [sortState, setSortState] = useState<TableSortState>(null);
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [bulkAction, setBulkAction] = useState<string>("");
  const [showBulkConfirmModal, setShowBulkConfirmModal] = useState(false);
  const [showToggleConfirmModal, setShowToggleConfirmModal] = useState(false);
  const [toggleRow, setToggleRow] = useState<TripDaysThresholdRow | null>(null);
  const historyPanel = useHistoryPanel("trip_days_threshold");

  const { data: tripDaysThresholdsData, isLoading, refetch } = useTripDaysServiceV1ListTripDaysThresholdsQuery({
    page: currentPage,
    size: rowsPerPage,
    sort: sortState?.columnId ? (sortFieldMap[sortState.columnId] ?? sortState.columnId) : "created_at",
    order: sortState?.direction ?? "desc",
    status: filterValues.status || undefined,
    tripDaysThresholdName: filterValues.tripDaysName || undefined,
    duration: filterValues.durationCondition || undefined,
    equivalentDays: filterValues.equivalentDays || undefined,
    description: filterValues.description || undefined,
    createdAtFrom: filterValues.createdAt?.split("|")[0] || undefined,
    createdAtTo: filterValues.createdAt?.split("|")[1] || undefined,
    updatedAtFrom: filterValues.updatedAt?.split("|")[0] || undefined,
    updatedAtTo: filterValues.updatedAt?.split("|")[1] || undefined,
  });
  const [activateTripDaysThresholds] = useTripDaysServiceV1ActivateTripDaysThresholdsMutation();
  const [deactivateTripDaysThresholds] = useTripDaysServiceV1DeactivateTripDaysThresholdsMutation();

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-list"),
        hasBackButton: false,
      })
    );
  }, [dispatch, t]);

  const tableData: TripDaysThresholdRow[] = useMemo(() => {
    if (!tripDaysThresholdsData?.trip_days_thresholds) return [];
    return tripDaysThresholdsData.trip_days_thresholds.map((item: TripDaysV1TripDaysThresholdRes) => ({
      id: item.id || "",
      tripDaysName: item.trip_days_threshold_name || "",
      durationCondition: (item.duration_condition || "below") as any,
      durationMin: item.duration_min ?? null,
      durationMax: item.duration_max ?? null,
      equivalentDays: item.equivalent_days ?? 0,
      description: item.description ?? "",
      status: (item.status || "inactive") as "active" | "inactive",
      createdBy: item.created_by || "",
      createdAt: item.created_at || "",
      updatedBy: item.updated_by || "",
      updatedAt: item.updated_at || "",
    }));
  }, [tripDaysThresholdsData]);

  const selectedRows = tableData.filter((row) => selectedKeys.includes(row.id));
  const hasMixedStatus =
    selectedRows.length > 0 &&
    new Set(selectedRows.map((r) => r.status)).size > 1;

  const isBulkActionDisabled =
    selectedKeys.length === 0 || hasMixedStatus;

  const handleApplyBulkAction = () => {
    if (!bulkAction || isBulkActionDisabled) return;
    setShowBulkConfirmModal(true);
  };

  const handleConfirmBulkAction = async () => {
    try {
      const ids = selectedKeys.map((key) => String(key));
      if (bulkAction === "activate") {
        await activateTripDaysThresholds({ tripDaysV1TripDaysBulkStatusChangeReq: { ids } }).unwrap();
        toast.success(t("toast-activate-success"));
      } else if (bulkAction === "deactivate") {
        await deactivateTripDaysThresholds({ tripDaysV1TripDaysBulkStatusChangeReq: { ids } }).unwrap();
        toast.success(t("toast-deactivate-success"));
      }
      setShowBulkConfirmModal(false);
      setBulkAction("");
      setSelectedKeys([]);
      refetch();
    } catch (error) {
      console.error("Bulk action failed:", error);
      errorHandler(error);
    }
  };

  const handleToggleStatus = (row: TripDaysThresholdRow) => {
    setToggleRow(row);
    setShowToggleConfirmModal(true);
  };

  const handleConfirmToggle = async () => {
    try {
      if (!toggleRow) return;
      const ids = [toggleRow.id];
      if (toggleRow.status === "active") {
        await deactivateTripDaysThresholds({ tripDaysV1TripDaysBulkStatusChangeReq: { ids } }).unwrap();
        toast.success(t("toast-deactivate-success"));
      } else {
        await activateTripDaysThresholds({ tripDaysV1TripDaysBulkStatusChangeReq: { ids } }).unwrap();
        toast.success(t("toast-activate-success"));
      }
      setShowToggleConfirmModal(false);
      setToggleRow(null);
      refetch();
    } catch (error) {
      console.error("Toggle failed:", error);
      errorHandler(error);
    }
  };

  const handleFilterChange = (values: Record<string, string>) => {
    setFilterValues(values);
    setCurrentPage(1);
  };

  const handleEdit = (row: TripDaysThresholdRow) => {
    navigate(editTripDaysThresholdPageRouteName(row.id.toString()));
  };

  const bulkConfirmMessage = useMemo(() => {
    if (selectedRows.length === 0) return "";
    return selectedRows[0]?.status === "active"
      ? t("bulk-confirm-deactivate")
      : t("bulk-confirm-activate");
  }, [selectedRows, t]);

  const columns: Columns<TripDaysThresholdRow>[] = useMemo(
    () => [
      {
        id: "no",
        fieldId: "index",
        label: t("col-no"),
        width: "60px",
      },
      {
        id: "tripDaysName",
        fieldId: "tripDaysName",
        label: t("label-trip-days-name"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
      },
      {
        id: "durationCondition",
        fieldId: "durationCondition",
        label: t("label-duration-condition"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
        render: (row) => (
          <span className="text-sm" data-testid={`col-duration-condition-${row.id}`}>{formatDuration(row, t("col-unit-hours"))}</span>
        ),
      },
      {
        id: "equivalentDays",
        fieldId: "equivalentDays",
        label: t("label-equivalent-days"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
        render: (row) => (
          <span className="text-sm" data-testid={`col-equivalent-days-${row.id}`}>{formatEquivalentDays(row.equivalentDays, t("col-unit-day"), t("col-unit-days"))}</span>
        ),
      },
      {
        id: "status",
        fieldId: "status",
        label: t("label-status"),
        sortable: true,
        filterable: true,
        filterOptions: [
          { label: t("col-all-data"), value: "" },
          { label: t("status-active"), value: "active" },
          { label: t("status-inactive"), value: "inactive" },
        ],
        render: (row) => (
          <div className="inline-flex items-center gap-2 text-sm" data-testid={`col-status-${row.id}`}>
            <span
              className={`h-3 w-3 rounded-full ${
                row.status === "active"
                  ? "bg-primary-600 border-2 border-primary-100"
                  : "bg-greyScale-60 border-2 border-greyScale-90"
              }`}
            />
            <span className="capitalize">{row.status}</span>
          </div>
        ),
      },
      {
        id: "createdAt",
        fieldId: "createdBy",
        fieldId2: "createdAt",
        label: t("col-created-by"),
        sortable: true,
        filterable: true,
        renderFilter: ({ value, onChange }) => {
          const [from = "", to = ""] = value.split("|");
          return (
            <DateRangePicker
              fromValue={from}
              toValue={to}
              onChange={(f, t) => onChange(`${f}|${t}`)}
              reserveHelperSpace={false}
            />
          );
        },
      },
      {
        id: "updatedAt",
        fieldId: "updatedBy",
        fieldId2: "updatedAt",
        label: t("col-updated-by"),
        sortable: true,
        filterable: true,
        renderFilter: ({ value, onChange }) => {
          const [from = "", to = ""] = value.split("|");
          return (
            <DateRangePicker
              fromValue={from}
              toValue={to}
              onChange={(f, t) => onChange(`${f}|${t}`)}
              reserveHelperSpace={false}
            />
          );
        },
      },
      {
        id: "action",
        fieldId: "id",
        label: t("col-action"),
        width: "140px",
        dragDisabled: true,
        render: (row) => (
          <div
            className="flex items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              type="button"
              variant="outline"
              color="primary"
              size="small"
              iconLeft={<Icon name="edit-2" size={14} />}
              onClick={() => handleEdit(row)}
              data-testid="btn-edit-threshold"
            >
              {t("btn-edit")}
            </Button>
            <ActionMenu
              items={[
                {
                  label: row.status === "active" ? t("btn-deactivate") : t("btn-activate"),
                  onClick: () => handleToggleStatus(row),
                  variant: row.status === "active" ? "danger" : "default",
                  testId: row.status === "active" ? "btn-deactivate-threshold" : "btn-activate-threshold",
                },
                {
                  label: t("btn-track-history"),
                  onClick: () => historyPanel.openPanel(row.id, row.tripDaysName),
                  testId: "btn-track-history-threshold",
                },
              ]}
            />
          </div>
        ),
      },
    ],
    [t],
  );

  const totalPages = tripDaysThresholdsData?.metadata?.total_page ?? 1;

  return {
    tableData,
    isLoading,
    data: tableData,
    columns,
    sortState,
    setSortState,
    filterValues,
    setFilterValues,
    handleFilterChange,
    selectedKeys,
    setSelectedKeys,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    totalPages,
    navigate,
    bulkAction,
    setBulkAction,
    isBulkActionDisabled,
    showBulkConfirmModal,
    setShowBulkConfirmModal,
    handleApplyBulkAction,
    handleConfirmBulkAction,
    showToggleConfirmModal,
    setShowToggleConfirmModal,
    toggleRow,
    handleConfirmToggle,
    bulkConfirmMessage,
    historyPanel,
    t,
  };
};

export default useTripDaysThresholdList;
